import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { SmartList } from "./List";
import { withPagination } from "./withPagination";
import { debounce } from "../utils/debounce";
import { ThemeProvider } from "./theme";

const gutterWidth = 2;
const cardGridReflowBreakpoint = "45em";
const getRelativeOffset = (elem) => -1 * parseInt(elem.style.top || "0", 10);

const applyMasonryEffect = () => {
    // Masonry layout hack while attempting to maintain some sort of
    // keyboard/source order :(
    setTimeout(() => {
        const list = document.querySelector("#card-grid .card-grid__list");
        const items = Array.from(
            document.querySelectorAll("#card-grid .card-grid__list-item")
        );
        if (!list || !items.length) {
            return;
        }

        const columns = Math.round(list.clientWidth / items[0].clientWidth);

        if (columns <= 1) {
            items.forEach((item) => {
                item.style.position = "static";
                item.style.top = "0";
            });
            list.style.marginBottom = "0";
            return;
        }

        const offsets = items.map((item, i, arr) => {
            if (i < columns) {
                return 0;
            }

            const previousColItem = arr[i - columns];
            const previousColItemTrueBottom =
                previousColItem.offsetTop +
                getRelativeOffset(previousColItem) +
                previousColItem.clientHeight;

            const itemTrueTop = item.offsetTop + getRelativeOffset(item);
            return itemTrueTop - previousColItemTrueBottom;
        });

        const relativeOffsets = offsets.map((item, i, arr) => {
            return arr.slice(0, i).reduce((acc, m, j) => {
                return j % columns === i % columns ? acc + m : acc;
            }, item);
        });

        items.forEach((item, i) => {
            const movement = relativeOffsets[i];
            item.style.position = "relative";
            item.style.top = `-${movement}px`;
        });

        // FIXME: Does not handle non-multiples of 3 items very well. Buggy
        const deficit = Math.min(...relativeOffsets.slice(-1 * columns));
        list.style.marginBottom = `-${deficit}px`;
    }, 500);
};

const debouncedApplyMasonryEffect = debounce(applyMasonryEffect, 200);

function CardGrid({ id = "card-grid", cards }) {
    useEffect(() => {
        applyMasonryEffect();
    });

    useEffect(() => {
        window.addEventListener("resize", debouncedApplyMasonryEffect);
    }, []);

    return (
        <div id={id} className="card-grid">
            <ThemeProvider theme={{ cardGridReflowBreakpoint }}>
                <SmartList
                    solitaryItemClassName="card-grid__solitary-list-item"
                    listClassName="card-grid__list"
                    itemClassName="card-grid__list-item"
                    items={cards}
                />
            </ThemeProvider>
            <style jsx>{`
                .card-grid :global(.card-grid__solitary-list-item) {
                    margin: -${gutterWidth}em auto 0;
                    padding-top: ${gutterWidth}em;
                    box-sizing: border-box;
                    max-width: 30.75em;
                }
                @media (min-width: ${cardGridReflowBreakpoint}) {
                    .card-grid :global(.card-grid__solitary-list-item) {
                        margin-left: -${gutterWidth}em;
                        padding-left: ${gutterWidth}em;
                        margin-right: 0;
                    }
                }
                .card-grid :global(.card-grid__list) {
                    margin: -${gutterWidth}em 0 0 -${gutterWidth}em;
                    padding: 0;
                    list-style-type: none;

                    display: flex;
                    flex-wrap: wrap;
                    justify-content: center;
                    align-items: flex-start;
                }
                @media (min-width: ${cardGridReflowBreakpoint}) {
                    .card-grid :global(.card-grid__list) {
                        justify-content: flex-start;
                    }
                }
                .card-grid :global(.card-grid__list-item) {
                    padding: ${gutterWidth}em 0 0 ${gutterWidth}em;
                    box-sizing: border-box;
                    display: inline-block;
                    width: 100%;
                    max-width: 32em;
                    vertical-align: top;

                    flex: 0 1 auto;
                }
                @media (min-width: ${cardGridReflowBreakpoint}) {
                    .card-grid :global(.card-grid__list-item),
                    .card-grid :global(.card-grid__solitary-list-item) {
                        min-width: 21em;
                        width: 50%;
                        max-width: 50%;
                    }
                }
                @media (min-width: 66em) {
                    .card-grid :global(.card-grid__list-item),
                    .card-grid :global(.card-grid__solitary-list-item) {
                        min-width: 21em;
                        width: 33.33%;
                        max-width: 33.33%;
                    }
                }
            `}</style>
        </div>
    );
}

CardGrid.propTypes = {
    cards: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            display: PropTypes.node,
        })
    ).isRequired,
};

export const PaginatedCardGrid = withPagination(CardGrid, "cards");
